import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Sparkles, Zap, Leaf, Droplet, Atom, Cpu, Sun, Cloud, Shield, ShoppingCart, DollarSign, Radio, Gamepad2, Rocket, Droplets, Bot, Building, Plus, X, Trash2, Edit, Layers, LayoutGrid, List, Crown, Check } from 'lucide-react';
import { db } from '../firebase/config';
import { doc, updateDoc, arrayUnion, arrayRemove, onSnapshot, collection, getDocs, addDoc, deleteDoc, serverTimestamp, query, where, getDoc } from 'firebase/firestore';
import { USER_ROLES, canEditDefaultThemes } from '../utils/roles';
import CompanyLogo from './CompanyLogo';
import { useTranslation } from 'react-i18next';
import AuthModal from './AuthModal';

const ThemesPage = ({ currentUser, guestThemes, onSelectTheme }) => {
  console.log('[ThemesPage] Component render. Props:', { currentUser: currentUser?.uid, guestThemes });
  const { t } = useTranslation();
  const [watchlistThemes, setWatchlistThemes] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-themes'); // 'my-themes' or 'all-themes'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [newThemeName, setNewThemeName] = useState('');
  const [newThemeDescription, setNewThemeDescription] = useState('');
  const [newThemeColor, setNewThemeColor] = useState('#3b82f6');
  const [newThemeIcon, setNewThemeIcon] = useState('sparkles');
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [userRole, setUserRole] = useState(USER_ROLES.GUEST);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [modalType, setModalType] = useState('warning');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  // Icon mapping for different theme types
  const iconMap = {
    'crown': Crown,
    'flash': Zap,
    'water': Droplet,
    'hardware-chip': Cpu,
    'sunny': Sun,
    'link': Atom,
    'cloud': Cloud,
    'shield-checkmark': Shield,
    'cart': ShoppingCart,
    'medical': Sparkles,
    'cash': DollarSign,
    'cellular': Radio,
    'game-controller': Gamepad2,
    'rocket': Rocket,
    'water-outline': Droplets,
    'construct': Bot,
    'business': Building,
    'sparkles': Sparkles
  };

  // Color mapping for gradient classes
  const getColorClasses = (color) => {
    const colorMap = {
      '#f59e0b': { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', border: 'border-amber-200' },
      '#06b6d4': { gradient: 'from-cyan-500 to-blue-600', bg: 'bg-cyan-50', border: 'border-cyan-200' },
      '#8b5cf6': { gradient: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50', border: 'border-purple-200' },
      '#10b981': { gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50', border: 'border-green-200' },
      '#f97316': { gradient: 'from-orange-500 to-red-600', bg: 'bg-orange-50', border: 'border-orange-200' },
      '#3b82f6': { gradient: 'from-blue-500 to-cyan-600', bg: 'bg-blue-50', border: 'border-blue-200' },
      '#ef4444': { gradient: 'from-red-500 to-pink-600', bg: 'bg-red-50', border: 'border-red-200' },
      '#ec4899': { gradient: 'from-pink-500 to-purple-600', bg: 'bg-pink-50', border: 'border-pink-200' },
      '#14b8a6': { gradient: 'from-teal-500 to-cyan-600', bg: 'bg-teal-50', border: 'border-teal-200' },
      '#22c55e': { gradient: 'from-lime-500 to-green-600', bg: 'bg-lime-50', border: 'border-lime-200' },
      '#a855f7': { gradient: 'from-purple-600 to-pink-600', bg: 'bg-purple-50', border: 'border-purple-200' },
      '#6366f1': { gradient: 'from-indigo-500 to-blue-600', bg: 'bg-indigo-50', border: 'border-indigo-200' },
      '#0ea5e9': { gradient: 'from-sky-500 to-blue-600', bg: 'bg-sky-50', border: 'border-sky-200' },
      '#84cc16': { gradient: 'from-lime-600 to-green-600', bg: 'bg-lime-50', border: 'border-lime-200' }
    };
    return colorMap[color] || { gradient: 'from-gray-500 to-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
  };

  // Load available stocks from API
  useEffect(() => {
    const loadStocks = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/companies`);
        const data = await response.json();
        if (data.success) {
          setAvailableStocks(data.companies);
        }
      } catch (error) {
        console.error('Error loading stocks:', error);
      }
    };

    loadStocks();
  }, []);

  // Load themes from Firebase
  useEffect(() => {
    console.log('[ThemesPage] useEffect loadThemes triggered. guestThemes:', guestThemes);
    const loadThemes = async () => {
      try {
        const themesCollection = collection(db, 'investmentThemes');
        const themesSnapshot = await getDocs(themesCollection);
        const themesData = themesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('[ThemesPage] Loaded themes from Firebase:', themesData.length);

        // Sort themes alphabetically by name
        themesData.sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        });

        // Add guest themes if provided
        if (guestThemes && guestThemes.length > 0) {
          console.log('[ThemesPage] Adding guest themes:', guestThemes);
          guestThemes.forEach(guestTheme => {
            themesData.push({
              id: guestTheme.id,
              name: guestTheme.name,
              description: guestTheme.description,
              companies: guestTheme.companies,
              icon: 'sparkles',
              color: '#3b82f6',
              isGuestTheme: true
            });
          });
          console.log('[ThemesPage] Total themes after adding guest:', themesData.length);
        } else {
          console.log('[ThemesPage] No guest themes to add');
        }

        setThemes(themesData);
      } catch (error) {
        console.error('Error loading themes:', error);
      }
    };

    loadThemes();
  }, [guestThemes]);

  // Load user's watchlist themes and role
  useEffect(() => {
    console.log('[ThemesPage] useEffect watchlistThemes triggered. currentUser:', currentUser?.uid, 'guestThemes:', guestThemes);
    if (!currentUser?.uid) {
      setLoading(false);
      setUserRole(USER_ROLES.GUEST);
      // Set guest themes as selected in watchlist
      if (guestThemes && guestThemes.length > 0) {
        const guestThemeIds = guestThemes.map(theme => theme.id);
        console.log('[ThemesPage] Setting watchlistThemes for guest:', guestThemeIds);
        setWatchlistThemes(guestThemeIds);
      } else {
        console.log('[ThemesPage] No guest themes, not setting watchlistThemes');
      }
      return;
    }

    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setWatchlistThemes(userData.watchlistThemes || []);
          setUserRole(userData.userRole || USER_ROLES.MEMBER);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, guestThemes]);

  const toggleTheme = async (themeId) => {
    if (!currentUser?.uid) {
      setModalType('warning');
      setModalTitle(t('themes.loginRequired') || '로그인 필요');
      setModalMessage(t('themes.loginRequiredMessage') || '테마를 추가하려면 로그인이 필요합니다.');
      setShowWarningModal(true);
      return;
    }

    // Check if user is a guest
    if (userRole === USER_ROLES.GUEST) {
      setModalType('warning');
      setModalTitle(t('themes.guestRestriction') || '게스트 제한');
      setModalMessage(t('themes.guestRestrictionMessage') || '게스트는 테마를 추가할 수 없습니다.\n로그인 후 이용해주세요.');
      setShowWarningModal(true);
      return;
    }

    const isInWatchlist = watchlistThemes.includes(themeId);

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      if (isInWatchlist) {
        await updateDoc(userRef, { watchlistThemes: arrayRemove(themeId) });
      } else {
        await updateDoc(userRef, { watchlistThemes: arrayUnion(themeId) });
      }
    } catch (error) {
      console.error('Error toggling theme:', error);
      setModalType('error');
      setModalTitle(t('themes.updateFailed') || '업데이트 실패');
      setModalMessage(t('themes.updateFailedMessage') || '테마 업데이트에 실패했습니다. 다시 시도해주세요.');
      setShowWarningModal(true);
    }
  };

  const handleThemeClick = (themeId) => {
    if (onSelectTheme) {
      onSelectTheme(themeId);
    }
  };

  const handleCreateTheme = async () => {
    if (!currentUser?.uid) {
      alert('Please sign in to create themes');
      return;
    }

    if (!newThemeName.trim()) {
      alert('Please enter a theme name');
      return;
    }

    if (selectedStocks.length === 0) {
      alert('Please select at least one stock');
      return;
    }

    try {
      if (editingTheme) {
        // Update existing theme
        console.log('[ThemesPage] Updating theme:', editingTheme.id);

        const themeRef = doc(db, 'investmentThemes', editingTheme.id);
        await updateDoc(themeRef, {
          name: newThemeName,
          description: newThemeDescription,
          icon: newThemeIcon,
          color: newThemeColor,
          stocks: selectedStocks.map(stock => ({
            ticker: stock.ticker,
            name: stock.name,
            price: 0,
            changePercent: 0,
            volume: '0'
          })),
          stockCount: selectedStocks.length,
          updatedAt: serverTimestamp()
        });

        console.log('[ThemesPage] Theme updated successfully');
        alert('Theme updated successfully!');
      } else {
        // Create new theme
        console.log('[ThemesPage] Creating theme...');
        console.log('[ThemesPage] User ID:', currentUser.uid);
        console.log('[ThemesPage] Theme name:', newThemeName);
        console.log('[ThemesPage] Selected stocks:', selectedStocks.length);

        const themeData = {
          name: newThemeName,
          description: newThemeDescription,
          icon: newThemeIcon,
          color: newThemeColor,
          stocks: selectedStocks.map(stock => ({
            ticker: stock.ticker,
            name: stock.name,
            price: 0,
            changePercent: 0,
            volume: '0'
          })),
          stockCount: selectedStocks.length,
          changePercent: 0,
          trending: 'neutral',
          createdBy: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isCustom: !canEditDefaultThemes(userRole), // Admin themes are official (not custom)
          isOfficial: canEditDefaultThemes(userRole) // Mark admin-created themes as official
        };

        console.log('[ThemesPage] Theme data:', themeData);

        const docRef = await addDoc(collection(db, 'investmentThemes'), themeData);
        console.log('[ThemesPage] Theme created with ID:', docRef.id);

        // Add to user's watchlist
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          watchlistThemes: arrayUnion(docRef.id)
        });

        alert('Theme created successfully!');
      }

      // Reset form
      setNewThemeName('');
      setNewThemeDescription('');
      setNewThemeColor('#3b82f6');
      setNewThemeIcon('sparkles');
      setSelectedStocks([]);
      setStockSearch('');
      setShowCreateModal(false);
      setEditingTheme(null);

      // Reload themes
      const themesCollection = collection(db, 'investmentThemes');
      const themesSnapshot = await getDocs(themesCollection);
      const themesData = themesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setThemes(themesData);

    } catch (error) {
      console.error('[ThemesPage] Error creating/updating theme:', error);
      console.error('[ThemesPage] Error code:', error.code);
      console.error('[ThemesPage] Error message:', error.message);

      if (error.code === 'permission-denied') {
        alert('Permission denied: You do not have permission to create/update themes. Please check Firestore security rules.');
      } else {
        alert(`Failed to create/update theme: ${error.message}`);
      }
    }
  };

  const handleEditTheme = (theme) => {
    if (!currentUser?.uid) {
      alert('Please sign in to edit themes');
      return;
    }

    // Check permissions:
    // 1. Users can edit their own custom themes
    // 2. Admins can edit any theme (including default themes)
    const isOwnCustomTheme = theme.isCustom && theme.createdBy === currentUser.uid;
    const isAdmin = canEditDefaultThemes(userRole);

    if (!isOwnCustomTheme && !isAdmin) {
      alert('You can only edit themes you created. Admins can edit all themes.');
      return;
    }

    // Populate form with existing theme data
    setEditingTheme(theme);
    setNewThemeName(theme.name);
    setNewThemeDescription(theme.description || '');
    setNewThemeColor(theme.color || '#3b82f6');
    setNewThemeIcon(theme.icon || 'sparkles');

    // Convert theme stocks to available stocks format
    const themeStocks = (theme.stocks || []).map(stock => ({
      ticker: stock.ticker,
      name: stock.name
    }));
    setSelectedStocks(themeStocks);
    setShowCreateModal(true);
  };

  const handleDeleteTheme = async (themeId, theme) => {
    if (!currentUser?.uid) {
      alert('Please sign in to delete themes');
      return;
    }

    // Check permissions: users can delete their own custom themes, admins can delete any theme
    const isOwnCustomTheme = theme.isCustom && theme.createdBy === currentUser.uid;
    const isAdmin = canEditDefaultThemes(userRole);

    if (!isOwnCustomTheme && !isAdmin) {
      alert('You can only delete themes you created');
      return;
    }

    // Show appropriate confirmation message
    const confirmMessage = isAdmin && !isOwnCustomTheme
      ? `Are you sure you want to delete "${theme.name}"? (Admin deletion)`
      : `Are you sure you want to delete "${theme.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'investmentThemes', themeId));

      // Remove from watchlist if present
      if (watchlistThemes.includes(themeId)) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { watchlistThemes: arrayRemove(themeId) });
      }

      // Reload themes with alphabetical sorting
      const themesCollection = collection(db, 'investmentThemes');
      const themesSnapshot = await getDocs(themesCollection);
      const themesData = themesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort themes alphabetically by name
      themesData.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
      });

      setThemes(themesData);

      alert('Theme deleted successfully!');
    } catch (error) {
      console.error('Error deleting theme:', error);
      alert('Failed to delete theme. Please try again.');
    }
  };

  const toggleStockSelection = (stock) => {
    setSelectedStocks(prev => {
      const isSelected = prev.some(s => s.ticker === stock.ticker);
      if (isSelected) {
        return prev.filter(s => s.ticker !== stock.ticker);
      } else {
        return [...prev, stock];
      }
    });
  };

  const filteredStocks = availableStocks.filter(stock =>
    stock.ticker.toLowerCase().includes(stockSearch.toLowerCase()) ||
    stock.name.toLowerCase().includes(stockSearch.toLowerCase())
  );

  if (loading && themes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Get themes that are in watchlist
  console.log('[ThemesPage RENDER] calculating myThemes:', {
    themesCount: themes.length,
    watchlistThemesCount: watchlistThemes.length,
    watchlistThemes: watchlistThemes,
    guestThemesCount: guestThemes?.length,
    themesIds: themes.map(t => t.id),
    themesDetails: themes.map(t => ({ id: t.id, name: t.name, isGuestTheme: t.isGuestTheme }))
  });
  const myThemes = themes.filter(theme => watchlistThemes.includes(theme.id));
  console.log('[ThemesPage RENDER] myThemes result:', {
    count: myThemes.length,
    names: myThemes.map(t => t.name),
    ids: myThemes.map(t => t.id)
  });

  // Separate member-created and admin-created themes
  const memberThemes = themes.filter(theme => theme.isCustom === true);
  const adminThemes = themes.filter(theme => theme.isCustom !== true);

  const tabs = [
    { id: 'my-themes', label: t('themes.myThemes'), icon: Sparkles, count: myThemes.length, showOnlyForUser: true },
    { id: 'all-themes', label: t('themes.allThemes'), icon: Layers, count: themes.length }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <nav className="flex gap-2 flex-1">
                {tabs.map((tab) => {
                  if (tab.showOnlyForUser && !currentUser && (!guestThemes || guestThemes.length === 0)) return null;
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const showCount = tab.id === 'my-themes';

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        relative py-4 px-6 rounded-xl font-bold text-sm transition-all duration-200 flex-1
                        ${isActive
                          ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-inner translate-y-1'
                          : 'bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 shadow-md hover:shadow-lg hover:from-gray-100 hover:to-gray-200 active:translate-y-1 active:shadow-inner'
                        }
                        border-b-4 ${isActive ? 'border-blue-800' : 'border-gray-300'}
                      `}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive && tab.id === 'my-themes' ? 'fill-white' : ''}`} />
                        <span className="text-xs leading-tight">{tab.label}</span>
                        {showCount && (
                          <span className={`
                            inline-block min-w-[1.5rem] py-0.5 px-1.5 rounded-full text-xs font-extrabold text-center
                            ${isActive
                              ? 'bg-white/25 text-white ring-1 ring-white/30'
                              : 'bg-blue-100 text-blue-700'
                            }
                          `}>
                            {tab.count}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </nav>
              {currentUser && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-3 ml-2 rounded-xl hover:bg-blue-700 transition-colors shadow-md"
                >
                  <Plus className="w-5 h-5" />
                  Create
                </button>
              )}
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex justify-end mb-4">
          <div className="bg-white rounded-xl shadow-md p-1 inline-flex gap-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === 'grid'
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              {t('themes.gridView')}
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                viewMode === 'table'
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
              {t('themes.tableView')}
            </button>
          </div>
        </div>

        {/* My Themes Tab Content */}
        {activeTab === 'my-themes' && (
          <div>
            {(!currentUser && (!guestThemes || guestThemes.length === 0)) ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                  <Sparkles className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Please log in</h3>
                <p className="text-gray-600 text-lg">
                  Log in to create and manage your investment themes
                </p>
              </div>
            ) : myThemes.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-100 to-yellow-200 mb-4">
                  <Sparkles className="w-10 h-10 text-yellow-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No themes in your watchlist</h3>
                <p className="text-gray-600 text-lg mb-4">
                  Start adding investment themes to track them here!
                </p>
                <button
                  onClick={() => setActiveTab('all-themes')}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Browse All Themes
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {myThemes.map(theme => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    Icon={iconMap[theme.icon] || Sparkles}
                    colorClasses={getColorClasses(theme.color)}
                    isInWatchlist={true}
                    onToggle={toggleTheme}
                    onClick={handleThemeClick}
                    onEdit={handleEditTheme}
                    onDelete={handleDeleteTheme}
                    currentUserId={currentUser?.uid}
                    userRole={userRole}
                  />
                ))}
              </div>
            ) : (
              // Table View
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Theme
                        </th>
                        <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Stocks
                        </th>
                        <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Performance
                        </th>
                        <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {myThemes.map((theme, index) => {
                        const Icon = iconMap[theme.icon] || Sparkles;
                        const colorClasses = getColorClasses(theme.color);
                        const getTrendingIcon = () => {
                          if (theme.trending === 'up') return <TrendingUp className="w-4 h-4 text-green-600" />;
                          if (theme.trending === 'down') return <TrendingDown className="w-4 h-4 text-red-600" />;
                          return <Minus className="w-4 h-4 text-gray-600" />;
                        };
                        const isPositive = theme.changePercent >= 0;

                        return (
                          <tr
                            key={theme.id}
                            className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent cursor-pointer transition-all duration-200 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }`}
                            onClick={() => handleThemeClick(theme.id)}
                          >
                            <td className="px-3 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center flex-shrink-0`}>
                                  <Icon className="w-5 h-5 text-white" />
                                </div>
                                <div className="font-bold text-gray-900">{theme.name}</div>
                              </div>
                            </td>
                            <td className="px-3 py-4">
                              <div className="text-sm text-gray-600 max-w-xs truncate">
                                {theme.description || 'No description'}
                              </div>
                            </td>
                            <td className="px-2 py-4 text-center whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                                {theme.stockCount || (theme.stocks?.length || 0)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-center whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                {getTrendingIcon()}
                                <span className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                  {theme.changePercent > 0 ? '+' : ''}{theme.changePercent}%
                                </span>
                              </div>
                            </td>
                            <td className="px-2 py-4 text-center whitespace-nowrap">
                              {theme.isOfficial ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                  Official
                                </span>
                              ) : theme.isCustom ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                                  Custom
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                                  Default
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-1">
                                {(theme.isCustom && theme.createdBy === currentUser?.uid) && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditTheme(theme);
                                      }}
                                      className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                      title="Edit theme"
                                    >
                                      <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTheme(theme.id, theme);
                                      }}
                                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                      title="Delete theme"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                {canEditDefaultThemes(userRole) && !theme.isCustom && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditTheme(theme);
                                    }}
                                    className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                    title="Edit theme (Admin)"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTheme(theme.id);
                                  }}
                                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-1.5"
                                  title="Remove from My Themes"
                                >
                                  <Check className="w-4 h-4" />
                                  <span>Added</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Themes Tab Content */}
        {activeTab === 'all-themes' && (
          <div>
            {themes.length === 0 && !loading ? (
              <div className="bg-white rounded-2xl shadow-xl p-12 text-center border border-gray-100">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 mb-4">
                  <Layers className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No themes available</h3>
                <p className="text-gray-600 text-lg">
                  Investment themes will appear here once loaded from the database.
                </p>
              </div>
            ) : viewMode === 'grid' ? (
              // Grid View
              <div className="space-y-8">
                {/* Member-Created Themes */}
                {memberThemes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md">
                        <Sparkles className="w-4 h-4 text-white" />
                        <h3 className="text-base font-bold text-white">Member Themes</h3>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-purple-300 to-transparent"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {memberThemes.map(theme => {
                        const isInWatchlist = watchlistThemes.includes(theme.id);
                        return (
                          <ThemeCard
                            key={theme.id}
                            theme={theme}
                            Icon={iconMap[theme.icon] || Sparkles}
                            colorClasses={getColorClasses(theme.color)}
                            isInWatchlist={isInWatchlist}
                            onToggle={toggleTheme}
                            onClick={handleThemeClick}
                            onEdit={handleEditTheme}
                            onDelete={handleDeleteTheme}
                            currentUserId={currentUser?.uid}
                            userRole={userRole}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Admin-Created Themes */}
                {adminThemes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
                        <Layers className="w-4 h-4 text-white" />
                        <h3 className="text-base font-bold text-white">Official Themes</h3>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-transparent"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {adminThemes.map(theme => {
                        const isInWatchlist = watchlistThemes.includes(theme.id);
                        return (
                          <ThemeCard
                            key={theme.id}
                            theme={theme}
                            Icon={iconMap[theme.icon] || Sparkles}
                            colorClasses={getColorClasses(theme.color)}
                            isInWatchlist={isInWatchlist}
                            onToggle={toggleTheme}
                            onClick={handleThemeClick}
                            onEdit={handleEditTheme}
                            onDelete={handleDeleteTheme}
                            currentUserId={currentUser?.uid}
                            userRole={userRole}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Table View
              <div className="space-y-6">
                {/* Member-Created Themes Table */}
                {memberThemes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md">
                        <Sparkles className="w-4 h-4 text-white" />
                        <h3 className="text-base font-bold text-white">Member Themes</h3>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-purple-300 to-transparent"></div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Theme
                              </th>
                              <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Stocks
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Performance
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-3 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {memberThemes.map((theme, index) => {
                              const Icon = iconMap[theme.icon] || Sparkles;
                              const colorClasses = getColorClasses(theme.color);
                              const isInWatchlist = watchlistThemes.includes(theme.id);
                              const getTrendingIcon = () => {
                                if (theme.trending === 'up') return <TrendingUp className="w-4 h-4 text-green-600" />;
                                if (theme.trending === 'down') return <TrendingDown className="w-4 h-4 text-red-600" />;
                                return <Minus className="w-4 h-4 text-gray-600" />;
                              };
                              const isPositive = theme.changePercent >= 0;

                              return (
                                <tr
                                  key={theme.id}
                                  className={`hover:bg-gradient-to-r hover:from-purple-50 hover:to-transparent cursor-pointer transition-all duration-200 ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                  }`}
                                  onClick={() => handleThemeClick(theme.id)}
                                >
                                  <td className="px-3 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center flex-shrink-0`}>
                                        <Icon className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="font-bold text-gray-900">{theme.name}</div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-4">
                                    <div className="text-sm text-gray-600 max-w-xs truncate">
                                      {theme.description || 'No description'}
                                    </div>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                                      {theme.stockCount || (theme.stocks?.length || 0)}
                                    </span>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-1">
                                      {getTrendingIcon()}
                                      <span className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                        {theme.changePercent > 0 ? '+' : ''}{theme.changePercent}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                                      Custom
                                    </span>
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      {theme.createdBy === currentUser?.uid && (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditTheme(theme);
                                            }}
                                            className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                            title="Edit theme"
                                          >
                                            <Edit className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteTheme(theme.id, theme);
                                            }}
                                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                            title="Delete theme"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTheme(theme.id);
                                        }}
                                        className={`px-3 py-2 rounded-lg transition-colors font-medium text-sm flex items-center gap-1.5 ${
                                          isInWatchlist
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                        title={isInWatchlist ? 'Remove from My Themes' : 'Add to My Themes'}
                                      >
                                        {isInWatchlist ? (
                                          <>
                                            <Check className="w-4 h-4" />
                                            <span>Added</span>
                                          </>
                                        ) : (
                                          <>
                                            <Plus className="w-4 h-4" />
                                            <span>Add</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin-Created Themes Table */}
                {adminThemes.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
                        <Layers className="w-4 h-4 text-white" />
                        <h3 className="text-base font-bold text-white">Official Themes</h3>
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-transparent"></div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Theme
                              </th>
                              <th className="px-3 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Stocks
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Performance
                              </th>
                              <th className="px-2 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-3 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {adminThemes.map((theme, index) => {
                              const Icon = iconMap[theme.icon] || Sparkles;
                              const colorClasses = getColorClasses(theme.color);
                              const isInWatchlist = watchlistThemes.includes(theme.id);
                              const getTrendingIcon = () => {
                                if (theme.trending === 'up') return <TrendingUp className="w-4 h-4 text-green-600" />;
                                if (theme.trending === 'down') return <TrendingDown className="w-4 h-4 text-red-600" />;
                                return <Minus className="w-4 h-4 text-gray-600" />;
                              };
                              const isPositive = theme.changePercent >= 0;

                              return (
                                <tr
                                  key={theme.id}
                                  className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent cursor-pointer transition-all duration-200 ${
                                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                  }`}
                                  onClick={() => handleThemeClick(theme.id)}
                                >
                                  <td className="px-3 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center flex-shrink-0`}>
                                        <Icon className="w-5 h-5 text-white" />
                                      </div>
                                      <div className="font-bold text-gray-900">{theme.name}</div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-4">
                                    <div className="text-sm text-gray-600 max-w-xs truncate">
                                      {theme.description || 'No description'}
                                    </div>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
                                      {theme.stockCount || (theme.stocks?.length || 0)}
                                    </span>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-1">
                                      {getTrendingIcon()}
                                      <span className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                        {theme.changePercent > 0 ? '+' : ''}{theme.changePercent}%
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-2 py-4 text-center whitespace-nowrap">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                                      Official
                                    </span>
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      {canEditDefaultThemes(userRole) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTheme(theme);
                                          }}
                                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                          title="Edit theme (Admin)"
                                        >
                                          <Edit className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleTheme(theme.id);
                                        }}
                                        className={`px-3 py-2 rounded-lg transition-colors font-medium text-sm flex items-center gap-1.5 ${
                                          isInWatchlist
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                        title={isInWatchlist ? 'Remove from My Themes' : 'Add to My Themes'}
                                      >
                                        {isInWatchlist ? (
                                          <>
                                            <Check className="w-4 h-4" />
                                            <span>Added</span>
                                          </>
                                        ) : (
                                          <>
                                            <Plus className="w-4 h-4" />
                                            <span>Add</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Theme Modal */}
        {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setShowCreateModal(false);
          setEditingTheme(null);
        }}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTheme ? 'Edit Theme' : 'Create Custom Theme'}
              </h2>
              <button onClick={() => {
                setShowCreateModal(false);
                setEditingTheme(null);
              }} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Theme Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Theme Name *</label>
                <input
                  type="text"
                  value={newThemeName}
                  onChange={(e) => setNewThemeName(e.target.value)}
                  placeholder="e.g., My Tech Stocks"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Theme Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newThemeDescription}
                  onChange={(e) => setNewThemeDescription(e.target.value)}
                  placeholder="Describe your theme..."
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Color and Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                  <input
                    type="color"
                    value={newThemeColor}
                    onChange={(e) => setNewThemeColor(e.target.value)}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
                  <select
                    value={newThemeIcon}
                    onChange={(e) => setNewThemeIcon(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sparkles">Sparkles</option>
                    <option value="flash">Lightning</option>
                    <option value="water">Water</option>
                    <option value="hardware-chip">Chip</option>
                    <option value="sunny">Sun</option>
                    <option value="cloud">Cloud</option>
                    <option value="shield-checkmark">Shield</option>
                    <option value="cart">Cart</option>
                    <option value="rocket">Rocket</option>
                  </select>
                </div>
              </div>

              {/* Stock Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Stocks * ({selectedStocks.length} selected)
                </label>
                <input
                  type="text"
                  value={stockSearch}
                  onChange={(e) => setStockSearch(e.target.value)}
                  placeholder="Search stocks..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
                />
                <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                  {filteredStocks.map((stock) => {
                    const isSelected = selectedStocks.some(s => s.ticker === stock.ticker);
                    return (
                      <div
                        key={stock.ticker}
                        onClick={() => toggleStockSelection(stock)}
                        className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-200 last:border-0 ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-blue-600">{stock.ticker}</span>
                            <span className="text-gray-700 ml-2">{stock.name}</span>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTheme(null);
                }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTheme}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingTheme ? 'Update Theme' : 'Create Theme'}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Warning Modal for Guest Users */}
      <AuthModal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        type={modalType}
        title={modalTitle}
        message={modalMessage}
      />
    </div>
  );
};

const ThemeCard = ({ theme, Icon, colorClasses, isInWatchlist, onToggle, onClick, onEdit, onDelete, currentUserId, userRole }) => {
  const [showPopup, setShowPopup] = React.useState(false);
  const [popupPosition, setPopupPosition] = React.useState({ x: 0, y: 0, width: 0, maxHeight: 500, placement: 'top' });
  const [companyDetails, setCompanyDetails] = React.useState({});
  const cardRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const hoverTimeoutRef = React.useRef(null);

  const getTrendingIcon = () => {
    if (theme.trending === 'up') return <TrendingUp className="w-3 h-3 text-green-600" />;
    if (theme.trending === 'down') return <TrendingDown className="w-3 h-3 text-red-600" />;
    return <Minus className="w-3 h-3 text-gray-600" />;
  };

  // Users can edit their own custom themes
  // Admins can edit any theme (including default themes)
  const isOwnCustomTheme = theme.isCustom && theme.createdBy === currentUserId;
  const isAdmin = canEditDefaultThemes(userRole);
  const canEdit = isOwnCustomTheme || isAdmin;
  const canDelete = isOwnCustomTheme || isAdmin; // Users can delete their own themes, admins can delete any theme

  const handleMouseEnter = async (e) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const maxPopupHeight = 500; // Maximum popup height
      const padding = 20; // Padding from viewport edges

      // Calculate available space
      const spaceAbove = rect.top - padding;
      const spaceBelow = viewportHeight - rect.bottom - padding;

      // Determine placement and calculate actual max height
      let placement, maxHeight;
      if (spaceAbove > spaceBelow && spaceAbove > 200) {
        // Place above
        placement = 'top';
        maxHeight = Math.min(maxPopupHeight, spaceAbove);
      } else {
        // Place below
        placement = 'bottom';
        maxHeight = Math.min(maxPopupHeight, spaceBelow);
      }

      setPopupPosition({
        x: rect.left + rect.width / 2,
        y: placement === 'top' ? rect.top - 10 : rect.bottom + 10,
        width: rect.width,
        maxHeight: maxHeight,
        placement
      });
    }

    // Fetch company details for tickers without full stock info
    if (theme.tickers && theme.tickers.length > 0 && Object.keys(companyDetails).length === 0) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiUrl}/api/companies`);
        const data = await response.json();
        if (data.success) {
          const detailsMap = {};
          data.companies.forEach(company => {
            detailsMap[company.ticker] = company;
          });
          setCompanyDetails(detailsMap);
        }
      } catch (error) {
        console.error('Error fetching company details:', error);
      }
    }

    setShowPopup(true);
  };

  const handleMouseLeave = (e) => {
    // Delay hiding to allow mouse to move to popup
    hoverTimeoutRef.current = setTimeout(() => {
      setShowPopup(false);
    }, 100);
  };

  const handlePopupMouseEnter = () => {
    // Cancel hide timeout when mouse enters popup
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handlePopupMouseLeave = () => {
    setShowPopup(false);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <React.Fragment>
      <div
        ref={cardRef}
        className={`border-2 ${colorClasses.border} ${colorClasses.bg} rounded-lg p-3 hover:shadow-lg transition-all cursor-pointer ${
          isInWatchlist ? 'ring-2 ring-blue-500' : ''
        } relative`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {(canEdit || canDelete) && (
          <div className="absolute top-2 right-2 flex gap-1 z-10">
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(theme);
                }}
                className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
                title={isAdmin && !isOwnCustomTheme ? "Edit theme (Admin)" : "Edit theme"}
              >
                <Edit className="w-3 h-3" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(theme.id, theme);
                }}
                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                title={isAdmin && !isOwnCustomTheme ? "Delete theme (Admin)" : "Delete theme"}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        <div onClick={() => onClick(theme.id)}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 pr-6">{theme.name}</h3>
          </div>

          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-gray-500">{theme.stockCount || (theme.stocks?.length || 0)} stocks</span>
            <div className="flex items-center gap-1">
              {getTrendingIcon()}
              <span className={theme.changePercent >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {theme.changePercent > 0 ? '+' : ''}{theme.changePercent}%
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle(theme.id);
          }}
          className={`w-full py-2 px-3 rounded-md text-xs font-medium transition-colors mt-3 flex items-center justify-center gap-1.5 ${
            isInWatchlist
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500 hover:text-blue-600'
          }`}
          title={isInWatchlist ? 'Remove from My Themes' : 'Add to My Themes'}
        >
          {isInWatchlist ? (
            <>
              <Check className="w-3.5 h-3.5" />
              <span>Added</span>
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              <span>Add</span>
            </>
          )}
        </button>
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-4 overflow-y-auto"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            width: `${popupPosition.width}px`,
            maxHeight: `${popupPosition.maxHeight}px`,
            transform: popupPosition.placement === 'top'
              ? 'translate(-50%, -100%)'
              : 'translate(-50%, 0)',
            pointerEvents: 'auto'
          }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          {/* Arrow pointing to the card */}
          <div
            className={`absolute left-1/2 transform -translate-x-1/2 ${
              popupPosition.placement === 'top' ? 'bottom-0 translate-y-full' : 'top-0 -translate-y-full'
            }`}
            style={{
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              ...(popupPosition.placement === 'top'
                ? { borderTop: '10px solid white' }
                : { borderBottom: '10px solid white' }
              ),
              filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))'
            }}
          />

          {/* Popup Content */}
          <div className="space-y-3">
            {/* Theme Header */}
            <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses.gradient} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900">{theme.name}</h3>
                <p className="text-xs text-gray-500">
                  {theme.stockCount || (theme.stocks?.length || 0)} stocks
                  {theme.isOfficial && <span className="ml-2 text-green-600 font-semibold">• Official</span>}
                  {theme.isCustom && <span className="ml-2 text-blue-600">• Custom Theme</span>}
                </p>
              </div>
            </div>

            {/* Description */}
            {theme.description && (
              <div className="text-sm text-gray-700">
                {theme.description}
              </div>
            )}

            {/* Performance */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <span className="text-sm text-gray-600">Performance</span>
              <div className="flex items-center gap-2">
                {getTrendingIcon()}
                <span className={`text-sm font-bold ${theme.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {theme.changePercent > 0 ? '+' : ''}{theme.changePercent}%
                </span>
              </div>
            </div>

            {/* Stock List */}
            {((theme.stocks && theme.stocks.length > 0) || (theme.tickers && theme.tickers.length > 0)) && (
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-2">Stocks:</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {theme.stocks && theme.stocks.length > 0 ? (
                    // Custom themes with stock objects
                    theme.stocks.slice(0, 10).map((stock) => (
                      <div
                        key={stock.ticker}
                        className="flex items-center justify-between bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <CompanyLogo ticker={stock.ticker} size="sm" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-gray-600 truncate" title={stock.name}>
                              {stock.name}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          {stock.price > 0 && (
                            <div className="text-sm text-gray-900 font-semibold">
                              ${stock.price?.toFixed(2)}
                            </div>
                          )}
                          <div className={`text-xs font-medium ${(stock.changePercent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(stock.changePercent || 0) > 0 ? '+' : ''}{(stock.changePercent || 0).toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Default themes with just tickers
                    theme.tickers.slice(0, 10).map((ticker) => {
                      const company = companyDetails[ticker];
                      return (
                        <div
                          key={ticker}
                          className="flex items-center justify-between bg-gray-50 rounded-lg p-2 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <CompanyLogo ticker={ticker} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-gray-900 font-medium">{ticker}</div>
                              {company && (
                                <div className="text-xs text-gray-600 truncate" title={company.name}>
                                  {company.name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            {company && company.price > 0 && (
                              <div className="text-sm text-gray-900 font-semibold">
                                ${company.price?.toFixed(2)}
                              </div>
                            )}
                            <div className={`text-xs font-medium ${(company?.changePercent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(company?.changePercent || 0) > 0 ? '+' : ''}{(company?.changePercent || 0).toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {(theme.stocks?.length > 10 || theme.tickers?.length > 10) && (
                    <div className="text-center text-xs text-gray-500 pt-2">
                      +{(theme.stocks?.length || theme.tickers?.length) - 10} more stocks
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Click to view details hint */}
            <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
              Click card to view full details
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default ThemesPage;
